import { CropStyleFillPattern } from "@omnia/shared/generated/enums";
import { classes } from "@omnia/shared/react/react";
import { isArray } from "../../omnia";
import { compareStyles } from "../changes";
import { BasicStyle } from "../types";
import { useCopiedStyle } from "./CopiedStyleContext";
import { IconButton } from "./IconButton";

export interface StylePreviewProps {
    color: number[];
    pattern: CropStyleFillPattern;
    onPaste?: (value: BasicStyle) => void;
}

const width = 50;
const height = 30;

export function StylePreview({ color, pattern, onPaste }: StylePreviewProps) {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);

    React.useEffect(() => {
        const context = canvasRef.current.getContext("2d");

        context.clearRect(0, 0, width, height);

        if (isArray(color)) {
            const fillStyle = pattern
                ? omnia.fields.map.createCropStylePattern(color, pattern, 1)
                : omnia.color.colorToCss(color);

            context.fillStyle = fillStyle;
            context.fillRect(0, 0, width, height);
        }
    }, [color, pattern]);

    const [copiedStyle, setCopiedStyle] = useCopiedStyle();

    /**
     * Whether the currently copied style is the current swatch's style.
     */
    const isCopiedStyle = compareStyles(copiedStyle, { color, pattern });

    /**
     * Whether the swatch can be copied from.
     */
    const canCopy = Boolean(onPaste) && color !== null && pattern !== null;

    /**
     * Whether the swatch can be pasted into.
     */
    const canPaste = Boolean(onPaste && copiedStyle);

    const handleCanvasClick = canPaste ? () => onPaste({ ...copiedStyle }) : undefined;

    const handleCopyButtonClick = () => setCopiedStyle(isCopiedStyle ? null : { color, pattern });

    return (
        <>
            <canvas
                className={classes("style-preview", {
                    "can-paste": canPaste,
                    "is-copied": isCopiedStyle,
                })}
                width={width}
                height={height}
                ref={canvasRef}
                onClick={handleCanvasClick}
                title={canPaste ? "Paste style" : null}
            />
            {canCopy ? (
                <IconButton
                    icon={classes(isCopiedStyle ? "fas" : "far", "fa-paintbrush")}
                    onClick={handleCopyButtonClick}
                    title={isCopiedStyle ? "Cancel copy" : "Copy style"}
                />
            ) : null}
        </>
    );
}
